import { assert } from './assert'
import { handleError } from './handle-error'
import { getNewSessionEndTime } from './session-expire'
import { Storage } from './storage'
import {
    Api,
    CustomerData,
    CustomerDataWithPreferences,
    EventView,
    GiftCardView,
    PartialEvent,
    PluginSession,
    ProductCategory,
    SerialTicketView,
    SessionUpdateResult,
} from './types'
import { handleGtmData } from './utils/handle-gtm-data'

let api: Api

export const initApi = (newApi: Api) => {
    api = newApi
}

const applyPartialEvent = (view: EventView, partialEvent: PartialEvent) => {
    const { state, update } = view

    const { ticketTypes, seats, calc, status, paymentFailed, completionData, products } =
        partialEvent

    // Reset timer after each change via API
    state.sessionEndTime = getNewSessionEndTime(state.sessionMinutes)

    state.ticketTypes = ticketTypes
    state.calc = calc
    state.seats = seats
    state.status = status
    state.paymentFailed = paymentFailed
    state.products = products

    state.completionData = completionData ?? {
        ticketsTitle: '',
        ticketLines: [],
        ticketsSentText: '',
        ticketsDownloadText: '',
        ticketsButton: {
            url: '',
            text: '',
        },
        invoiceSentText: '',
        invoiceDownloadText: '',
        invoiceButton: {
            url: '',
            text: '',
        },
        contactInfo: '',
    }

    update()
}

const timeout = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

const updateViaEventApi = async (view: EventView, callback: () => Promise<PartialEvent>) => {
    const { state, update } = view

    state.isLoading = true
    update()

    try {
        const [partialEvent] = await Promise.all([callback(), timeout(500)])
        applyPartialEvent(view, partialEvent)
    } finally {
        state.isLoading = false
        update()
    }
}

const getEventUuid = (view: EventView) => {
    const {
        state: { eventUuid },
    } = view
    assert(eventUuid)
    return eventUuid
}

export const EventActions = {
    loadGeneralData: async (view: EventView): Promise<void> => {
        const { state } = view

        try {
            const data = await api.getGeneralData()

            state.isProductSaleEnabled = data.isProductSaleEnabled
        } catch (error) {
            console.error(error)
        }
    },

    loadEvent: async (view: EventView): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        try {
            const event = await api.events.getEvent(eventUuid, state.selectedLang)
            const { externalTicketUrl } = event

            if (externalTicketUrl) {
                window.location.replace(externalTicketUrl)

                return
            }

            state.sessionMinutes = event.sessionMinutes ?? 10
            state.sessionEndTime = getNewSessionEndTime(state.sessionMinutes)
            state.details = event.details
            state.production = event.production
            state.floorplan = event.floorplan
            state.labels = event.labels
            state.status = event.status
            state.paymentFailed = event.paymentFailed
            state.hideDiscountCards = event.hideDiscountCards
            state.paymentOptions = event.paymentOptions ?? []
            state.isProductSaleEnabled = event.isProductSaleEnabled

            if (event.completionData) {
                state.completionData = event.completionData
            }

            if (event.gtm) {
                handleGtmData(event.gtm)
            }

            state.ticketTypes = event.ticketTypes
            state.calc = event.calc
            state.seats = event.seats

            const productCategories: Map<number, ProductCategory> = new Map()
            event.productCategories.forEach(function (value) {
                productCategories.set(value['id'], value)
            })

            state.productCategories = productCategories
            state.products = event.products

            if (event.status === 'checkout') {
                await EventActions.validateSelectedPaymentGateway(view)
            }
        } catch (error) {
            handleError(view, error as Error)
        }
    },

    addSeat: async (view: EventView, rowNumber: number, seatNumber: number): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.addSeat(eventUuid, state.selectedLang, rowNumber, seatNumber),
        )

        await EventActions.validateSelectedPaymentGateway(view)
    },

    setProductVariantQuantity: async (
        view: EventView,
        variantId: number,
        quantity: number,
    ): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.setProductVariantQuantity(
                eventUuid,
                state.selectedLang,
                variantId,
                quantity,
            ),
        )

        await EventActions.validateSelectedPaymentGateway(view)
    },

    removeSeat: async (view: EventView, rowNumber: number, seatNumber: number): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.removeSeat(eventUuid, state.selectedLang, rowNumber, seatNumber),
        )

        await EventActions.validateSelectedPaymentGateway(view)
    },

    changeSeatType: async (
        view: EventView,
        rowNumber: number,
        seatNumber: number,
        ticketTypeId: string,
    ): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.updateSeat(
                eventUuid,
                state.selectedLang,
                rowNumber,
                seatNumber,
                ticketTypeId,
            ),
        )
    },

    setTicketAmount: async (
        view: EventView,
        ticketTypeId: string,
        amount: number,
    ): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.setTicketAmount(eventUuid, state.selectedLang, ticketTypeId, amount),
        )

        await EventActions.validateSelectedPaymentGateway(view)
    },

    applyDiscount: async (view: EventView, code: string): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.applyDiscount(eventUuid, state.selectedLang, code),
        )
    },

    removeCard: async (view: EventView, code: string): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.removeCard(eventUuid, state.selectedLang, code),
        )
    },

    removeSerialTicket: async (view: EventView, serialTicketId: number): Promise<void> => {
        const { state } = view
        const eventUuid = getEventUuid(view)

        await updateViaEventApi(view, () =>
            api.events.removeSerialTicket(eventUuid, state.selectedLang, serialTicketId),
        )
    },

    validateSelectedPaymentGateway: async (view: EventView): Promise<void> => {
        const eventUuid = getEventUuid(view)
        const { state, update }: EventView = view

        const customer: CustomerData = {
            name: Storage.get('name').trim(),
            email: Storage.get('email').trim(),
            phone: Storage.get('phone').trim(),
        }

        const selectedPaymentGateway: string = Storage.get('selectedPaymentGateway')

        if (selectedPaymentGateway) {
            state.validationErrors = {}
            state.isLoading = true
            update()

            try {
                const result = await api.events.validateSelectedPaymentGateway(
                    eventUuid,
                    customer,
                    selectedPaymentGateway,
                )

                state.status = result.status

                if (result.status === 'checkout') {
                    if ('validationErrors' in result) {
                        state.validationErrors = result.validationErrors
                    }
                }
            } finally {
                state.isLoading = false
                update()
            }
        }
    },

    submit: async (view: EventView): Promise<void> => {
        const { state, update } = view

        const eventUuid = getEventUuid(view)

        const customer: CustomerDataWithPreferences = {
            name: Storage.get('name').trim(),
            email: Storage.get('email').trim(),
            phone: Storage.get('phone').trim(),
            newsletter: state.hasNewsletterCheckbox && Storage.get('newsletter') === 'yes',
        }

        const selectedPaymentGateway = Storage.get('selectedPaymentGateway')

        state.validationErrors = {}
        state.isLoading = true
        update()

        try {
            const returnUrl = window.location.href
            const result = await api.events.submit(
                eventUuid,
                customer,
                selectedPaymentGateway,
                returnUrl,
            )

            if (result.status === 'payment-pending') {
                const { redirectUrl } = result
                window.location.assign(redirectUrl)
                return
            }

            state.status = result.status

            if (result.status === 'checkout') {
                state.validationErrors = result.validationErrors
            }

            if (result.status === 'completed') {
                state.completionData = result.data
            }
        } finally {
            state.isLoading = false
            update()
        }
    },

    reset: async (view: EventView): Promise<void> => {
        const eventUuid = getEventUuid(view)
        await api.events.reset(eventUuid)
        window.location.assign(view.state.scheduleUrl)
    },

    loadSession: async (view: EventView): Promise<void> => {
        const { state } = view
        const session = await api.session.getSession()

        if (session?.eventUuid) {
            state.eventUuid = session.eventUuid

            // The API may send NULL values, but in client side, we must
            // to omit them from the object.
            if (session.rowNumber === null) {
                delete session.rowNumber
            }

            if (session.seatNumber === null) {
                delete session.seatNumber
            }

            state.session = session
        }
    },

    updateSession: async (session: PluginSession): Promise<SessionUpdateResult> =>
        api.session.updateSession(session),

    loadEventsData: async (view: EventView): Promise<void> => {
        const { state } = view
        state.eventsData = await api.events.getEvents()
    },
}

export const GiftCardActions = {
    getData: async () => api.giftCards.getData(),
    submit: async (view: GiftCardView): Promise<void> => {
        const { state, update } = view
        const { email } = state

        state.validationErrors = {}
        state.isLoading = true
        update()

        try {
            const amount = state.amount.replace(',', '.')
            const selectedPaymentGateway = Storage.get('selectedPaymentGateway')
            const returnUrl = window.location.href
            const result = await api.giftCards.submit(
                amount,
                email,
                returnUrl,
                selectedPaymentGateway,
            )

            if (result.status === 'payment-pending') {
                const { redirectUrl } = result
                window.location.assign(redirectUrl)
                return
            }

            if (result.status === 'checkout') {
                if (result.validationErrors) {
                    state.validationErrors = result.validationErrors
                }

                if (result.paymentFailed) {
                    assert(state.data)
                    state.data.paymentFailed = true
                }
            }
        } finally {
            state.isLoading = false
            update()
        }
    },
    reset: async (view: GiftCardView) => {
        const { state, update } = view

        state.isLoading = true
        update()

        await api.giftCards.reset()

        const { data } = state
        assert(data)
        data.status = 'checkout'
        data.paymentFailed = false
        delete data.completionData
        state.isLoading = false

        update()
    },
}

export const SerialTicketActions = {
    getData: async () => api.serialTickets.getData(),
    submit: async (view: SerialTicketView): Promise<void> => {
        const { state, update } = view
        const { typeId, email } = state

        state.validationErrors = {}
        state.isLoading = true
        update()

        try {
            const returnUrl = window.location.href
            const selectedPaymentGateway = Storage.get('selectedPaymentGateway')
            const result = await api.serialTickets.submit(
                typeId,
                email,
                returnUrl,
                selectedPaymentGateway,
            )

            if (result.status === 'payment-pending') {
                const { redirectUrl } = result
                window.location.assign(redirectUrl)
                return
            }

            if (result.status === 'checkout') {
                if (result.validationErrors) {
                    state.validationErrors = result.validationErrors
                }

                if (result.paymentFailed) {
                    assert(state.data)
                    state.data.paymentFailed = true
                }
            }
        } finally {
            state.isLoading = false
            update()
        }
    },
    reset: async (view: SerialTicketView) => {
        const { state, update } = view

        state.isLoading = true
        update()

        await api.serialTickets.reset()

        const { data } = state
        assert(data)
        data.status = 'checkout'
        data.paymentFailed = false
        delete data.completionData
        state.isLoading = false

        update()
    },
}
